// LoginModal.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService, { authHelpers, errorHelpers } from '../../utils/apiService';

const LoginModal = ({ onClose, onForgotPassword }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
        setAlert(null);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setAlert(null);

        try {
            const response = await apiService.auth.login(formData);

            // Store authentication data
            authHelpers.login(response.data.token, response.data.user);

            setAlert({
                type: 'success',
                message: 'Login successful! Redirecting to dashboard...'
            });

            // Redirect after short delay
            setTimeout(() => {
                navigate('/dashboard');
                onClose();
            }, 1000);

        } catch (error) {
            const errorMessage = errorHelpers.getErrorMessage(error);
            const errorCode = errorHelpers.getErrorCode(error);

            if (errorCode === 'EMAIL_NOT_VERIFIED') {
                setAlert({
                    type: 'warning',
                    message: errorMessage + ' Check your email for verification link.'
                });
            } else if (errorCode === 'ACCOUNT_DISABLED') {
                setAlert({
                    type: 'error',
                    message: errorMessage
                });
            } else {
                setAlert({
                    type: 'error',
                    message: errorMessage
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="modal-overlay" onClick={handleOverlayClick}>
            <div className="modal">
                <div className="modal-header">
                    <h2 className="modal-title">Login to Your Account</h2>
                    <button className="modal-close" onClick={onClose} type="button">
                        ×
                    </button>
                </div>

                <div className="modal-body">
                    {alert && (
                        <div className={`alert alert-${alert.type}`}>
                            {alert.message}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label className="form-label" htmlFor="email">
                                Email Address
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                className={`form-input ${errors.email ? 'error' : ''}`}
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter your email address"
                                disabled={loading}
                            />
                            {errors.email && (
                                <div className="form-error">{errors.email}</div>
                            )}
                        </div>

                        <div className="form-group">
                            <label className="form-label" htmlFor="password">
                                Password
                            </label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                className={`form-input ${errors.password ? 'error' : ''}`}
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="Enter your password"
                                disabled={loading}
                            />
                            {errors.password && (
                                <div className="form-error">{errors.password}</div>
                            )}
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary btn-block"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="loading-spinner mr-2"></span>
                                    Logging in...
                                </>
                            ) : (
                                'Login'
                            )}
                        </button>
                    </form>

                    <div className="text-center mt-4">
                        <button
                            type="button"
                            className="text-blue-600 hover:text-blue-800 bg-transparent border-none cursor-pointer"
                            onClick={onForgotPassword}
                            disabled={loading}
                        >
                            Forgot your password?
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginModal;